// S37: the bulk bar's script: the selection, the action it takes on every selected row, and keeping a
// card table's stuck bar off the focus. Select all is Gmail's (Ola's QA walk, 9 Oct): the header's
// checkbox takes the page drawn, and once a whole page is selected the bar offers every page.

// The room left between the stuck bar and a focus scrolled clear of it, in CSS px.
const BAR_GAP = 8;

// The element the page scrolls in: the app's layout scrolls a column, not the document.
function scrollParentOf(element) {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const { overflowY } = getComputedStyle(node);

        if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
            return node;
        }
    }

    return document.scrollingElement || document.documentElement;
}

export default function bulkBarPart({ TYPING_ANNOUNCE_MS }) {
    return {
        // -------------------------------------------------------------------------------- selection

        isSelected(id) {
            return this.selected[id] === true;
        },

        // A finished row is a notice, not a row to act on, so it is never selectable. Every page's: what the
        // selection may hold, and what the bar's Select all takes.
        selectableRows() {
            return this.matched.filter((row) => !this.notices[row.id]);
        },

        // The page drawn's selectable rows: what the header's checkbox takes.
        pageSelectableRows() {
            return this.visibleRows.filter((row) => !this.notices[row.id]);
        },

        // Every selected row the filters keep, on every page, in the list's order: what a bulk action sends.
        selectedIds() {
            return this.matched.filter((row) => this.selected[row.id]).map((row) => row.id);
        },

        selectedCount() {
            return Object.keys(this.selected).length;
        },

        toggleSelected(id) {
            if (this.selected[id]) {
                delete this.selected[id];
            } else {
                this.selected[id] = true;
            }

            this.announceSelection();
        },

        // The header's checkbox reads the page drawn: checked when every row on it is selected, mixed when
        // some are, whatever the other pages hold.
        isAllSelected() {
            const rows = this.pageSelectableRows();

            return rows.length > 0 && rows.every((row) => this.selected[row.id]);
        },

        isPartlySelected() {
            const rows = this.pageSelectableRows();
            const picked = rows.filter((row) => this.selected[row.id]).length;

            return picked > 0 && picked < rows.length;
        },

        // This page's rows, all on or all off. The other pages keep what is selected on them: Clear
        // selection is what empties every page.
        toggleAll() {
            const isAll = this.isAllSelected();

            this.pageSelectableRows().forEach((row) => {
                if (isAll) {
                    delete this.selected[row.id];
                } else {
                    this.selected[row.id] = true;
                }
            });

            this.announceSelection();
        },

        // Gmail's offer: once a whole page is selected and rows past it are not, the bar offers them all.
        canSelectEvery() {
            return this.isAllSelected() && this.selectedCount() < this.selectableRows().length;
        },

        selectEveryLabel() {
            return this.strings.selectEvery.replace(":count", this.selectableRows().length);
        },

        // Every row the filters keep, on every page; a filter changed after drops the rows it hides, as it
        // always has. The offer leaves with the press, so focus goes on to Clear selection beside it.
        selectEvery() {
            this.selectableRows().forEach((row) => {
                this.selected[row.id] = true;
            });

            this.announceSelection();
            this.afterShow(() => this.rootElement.querySelector("[data-clear-selection]")?.focus());
        },

        clearSelection() {
            this.selected = {};
            this.announceSelection();
            this.afterShow(() => this.focusSummary());
        },

        pruneSelection() {
            const kept = new Set(this.selectableRows().map((row) => row.id));

            Object.keys(this.selected).forEach((id) => {
                if (!kept.has(id)) {
                    delete this.selected[id];
                }
            });
        },

        announceSelection() {
            this.announce(this.strings.selected.replace(":count", this.selectedCount()), TYPING_ANNOUNCE_MS);
        },

        // "Select all 10 rows on this page" while the list has pages, at the page size the reader picked
        // (pager.js); a list of one page keeps its words.
        selectAllLabel() {
            return (this.hasPages() ? this.strings.selectPage : this.strings.selectAll).replace(":count", this.pageSelectableRows().length);
        },

        actOnSelection(action) {
            return this.act(action, this.selectedIds(), { kind: "bulk" });
        },

        // WCAG 2.4.11: a keyboard focus that lands under a card table's stuck bar (a row reached by Tab
        // while the page scrolls the list under it) is scrolled down clear of it. The browser scrolls a
        // focus into view only when it is off screen, and a control under a sticky bar is on screen.
        // Checked a frame later, once the browser's own scroll has run; a mouse focus is left alone, since
        // a click cannot land under the bar.
        keepFocusClearOfBar(event, bar) {
            const target = event.target;

            if (this.selectedCount() === 0 || !(target instanceof Element) || bar.contains(target)) {
                return;
            }

            requestAnimationFrame(() => {
                // A bar out of sight (another activity's, its pane hidden) covers nothing.
                if (bar.getClientRects().length === 0 || !target.matches(":focus-visible")) {
                    return;
                }

                const barBox = bar.getBoundingClientRect();
                const box = target.getBoundingClientRect();

                if (box.top < barBox.bottom + BAR_GAP && box.bottom > barBox.top) {
                    scrollParentOf(bar).scrollBy({ top: box.top - barBox.bottom - BAR_GAP });
                }
            });
        },
    };
}
